'use client';

import { useId } from 'react';
import { BlockRoot } from '@akademi/site-builder-runtime';
import { headingLevel, items, text, type HeadingLevel, type Localized } from '../shared/primitives';
import { SkBaseStyles, SkStyles } from '../styles/SkStyles';
import { homeSurfaceClass, HomeSurfaceStyles, JsonLdScript, type HomeSurface } from './homeShared';

/**
 * Questions and answers as native `<details>`/`<summary>`: they open and close
 * without JavaScript, and the answers are in the HTML, so crawlers and answer
 * engines read them.
 *
 * <p>Answers are plain text. React escapes them, so an operator cannot inject
 * markup. The same questions are emitted as `FAQPage` structured data unless
 * `structuredData` is `false` (turn it off when another block on the page
 * already describes the same questions); only pairs with both a question and an
 * answer are included, and the block emits nothing when there are none.
 */
export interface SpatialKitchenFaqItem {
  question?: Localized;
  answer?: Localized;
}

export interface SpatialKitchenFaqProps {
  eyebrow?: Localized;
  title?: Localized;
  items?: SpatialKitchenFaqItem[];
  /** Open the first question on load. */
  openFirst?: boolean;
  /** Emit `FAQPage` JSON-LD. On by default. */
  structuredData?: boolean;
  surface?: HomeSurface;
  headingLevel?: HeadingLevel;
}

const CSS = `
.sk-faq__list{max-width:860px}
.sk-faq__item{border-bottom:1px solid var(--sk-border);padding:20px 0}
.sk-faq__item summary{display:flex;justify-content:space-between;gap:20px;cursor:pointer;list-style:none;font-weight:700;font-size:1.05rem;line-height:1.4;color:var(--sk-ink)}
.sk-faq__item summary::-webkit-details-marker{display:none}
.sk-faq__item summary::after{content:'+';flex:none;font-size:1.4rem;line-height:1;color:var(--sk-brass-text);transition:transform .2s}
.sk-faq__item[open] summary::after{transform:rotate(45deg)}
.sk-faq__answer{margin:12px 0 0;color:var(--sk-muted);white-space:pre-line}
`;

export function SpatialKitchenFaq(props: SpatialKitchenFaqProps) {
  const faqs = items<SpatialKitchenFaqItem>(props.items)
    .map((item) => ({ question: text(item.question), answer: text(item.answer) }))
    .filter((item) => item.question);
  const complete = faqs.filter((item) => item.answer);
  const level = headingLevel(props.headingLevel, 'h2');
  const Title = level;
  const titleId = useId();
  const openFirst = props.openFirst !== false;

  return (
    <BlockRoot
      as="section"
      className={`sk sk-faq sk-section ${homeSurfaceClass(props.surface, 'stone')}`}
      aria-labelledby={titleId}
    >
      <SkBaseStyles />
      <HomeSurfaceStyles />
      <SkStyles id="faq" css={CSS} />
      <div className="sk-container">
        <div className="sk-section-head">
          {text(props.eyebrow) && <p className="sk-eyebrow">{text(props.eyebrow)}</p>}
          <Title id={titleId} className="sk-heading sk-h2">
            {text(props.title, 'Frequently asked questions')}
          </Title>
        </div>
        <div className="sk-faq__list">
          {faqs.map((item, index) => (
            <details key={`${item.question}-${index}`} className="sk-faq__item" open={openFirst && index === 0}>
              <summary>{item.question}</summary>
              {item.answer && <p className="sk-faq__answer">{item.answer}</p>}
            </details>
          ))}
        </div>
      </div>
      {props.structuredData !== false && complete.length > 0 && (
        <JsonLdScript
          data={{
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: complete.map((item) => ({
              '@type': 'Question',
              name: item.question,
              acceptedAnswer: { '@type': 'Answer', text: item.answer },
            })),
          }}
        />
      )}
    </BlockRoot>
  );
}
